import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BuyerInfoModel, InvoiceBuyerInfo, ProofOfPaymentOption } from '../models';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { ModalService } from '@indice/ng-components';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';
import { CountryCodeDropdownComponent } from '../../country-code-dropdown/country-code-dropdown.component';
import { OrderResponse } from 'src/app/core/services/payments-api-service';
import { tap } from 'rxjs';
import { UnikoServicesConfig, UnikoServiceTypes } from 'src/app/services/payments-api.service';

@Component({
  selector: 'app-step-buyer-info',
  standalone: true,
  imports: [
    CommonModule,
    LanguageRouterLinkDirective,
    ReactiveFormsModule,
    TranslocoDirective,
    TranslocoPipe,
    NgxMaskDirective,
    CountryCodeDropdownComponent
  ],
  templateUrl: './step-buyer-info.component.html',
  styleUrl: './step-buyer-info.component.css',
  providers: [provideNgxMask()],
})
export class StepBuyerInfoComponent implements OnInit, OnChanges {
  @Input() orderData: OrderResponse | undefined;
  @Input() oldState: BuyerInfoModel | undefined;
  @Output() formDataEvent = new EventEmitter<BuyerInfoModel>();
  protected ProofOfPaymentEnum = ProofOfPaymentOption; //makes the enum available in the html template
  protected proofOfPayment: ProofOfPaymentOption = ProofOfPaymentOption.Receipt;
  protected buyerForm: FormGroup;
  protected mainForm: FormGroup | undefined;
  private invoiceSubForm: FormGroup | undefined;
  private loading: boolean = false;
  protected countryCode?: string = "+30"; // set this as default
  protected termsDocumentType: string = 'services-document';
  private skuToTermsMap: Record<string, string> = { //This is a mapping of skus to cms' legal document codes
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.propertyUpload)?.sku!]: 'asset-onboarding',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.legalReport)?.sku!]: 'legal-report',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.technicalReport)?.sku!]: 'technical-report',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.preapproval)?.sku!]: 'property-certification',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.unikoExclusive)?.sku!]: 'uniko-exclusive',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.unikoPhotography)?.sku!]: 'uniko-exclusive',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.photography)?.sku!]: 'photography-service',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.propertyFileConstitution)?.sku!]: 'property-file-reconstitution',
    [UnikoServicesConfig.find(f => f.type == UnikoServiceTypes.biddingSupport)?.sku!]: 'bidding-support',
  };

  constructor(private _fb: FormBuilder, private _modalService: ModalService) {
    this.buyerForm = this._fb.group({
      mainForm: this._fb.group({
        proofOfPayment: [this.proofOfPayment, Validators.required],
        fullName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        address: ['', Validators.required],
        zipCode: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
        city: ['', Validators.required],
        mobilePhone: ['', [Validators.required, Validators.minLength(10)]],
        // termsConditionsAccepted: [false, Validators.requiredTrue],
        // paymentsTermsAccepted: [false],
        // serviceTermsAccepted: [false],
        landLine: [''],
      }),
    });

    this.buyerForm.addControl(
      'invoiceSubForm',
      this._fb.group({
        taxNumber: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9]{9}$')]], //'^[0-9]{9}$' for numbers only
        companyName: ['', Validators.required],
        taxOffice: ['', Validators.required],
        profession: ['', Validators.required],
      })
    );

    this.mainForm = this.buyerForm.get('mainForm') as FormGroup;
    this.invoiceSubForm = this.buyerForm.get('invoiceSubForm') as FormGroup;
    this.mainForm.get('proofOfPayment')?.valueChanges.pipe(
      tap(t => {
        if (t) this.proofOfPayment = t;
        else {
          this.proofOfPayment = ProofOfPaymentOption.Receipt;
          this.mainForm?.get('proofOfPayment')?.setValue(this.proofOfPayment);
        }
      })
    ).subscribe();
  }
  ngOnInit(): void {
    this.loadOldState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderData'].isFirstChange()) {
      this.termsDocumentType = this.mapSkuToCmsDocumentType();
      console.log(this.termsDocumentType);
    }
  }

  mapSkuToCmsDocumentType(): string {
    const skus = this.orderData?.orderItems?.map(m => m.sku);
    const cmsCodes = skus?.map(sku => {
      if (sku)
        return this.skuToTermsMap[sku];

      return null;
    }).filter(f => !!f);

    return cmsCodes?.at(0) ?? this.termsDocumentType;
  }

  public onSubmit(): boolean {
    if (this.isFormValid()) {
      this.emitFormData();

      return true;
    } else {
      this.markAllAsTouched(this.buyerForm);

      return false;
    }
  }

  // public onTermsChange() {
  //   this.mainForm?.get('termsConditionsAccepted')?.setValue(this.mainForm?.get('paymentsTermsAccepted')?.value && this.mainForm?.get('serviceTermsAccepted')?.value);
  // }

  private isFormValid(): boolean {
    if (!this.mainForm) return false;

    let subFormIsValidOrExcluded = true;
    if (this.proofOfPayment != ProofOfPaymentOption.Receipt) {
      subFormIsValidOrExcluded = this.invoiceSubForm?.valid ?? true;
    }

    const result = this.mainForm.valid && subFormIsValidOrExcluded;

    return result;
  }

  private emitFormData(): void {
    let result: BuyerInfoModel = {
      proofOfPayment: this.mainForm?.get('proofOfPayment')?.value,
      fullName: this.mainForm?.get('fullName')?.value,
      email: this.mainForm?.get('email')?.value,
      address: this.mainForm?.get('address')?.value,
      zipCode: this.mainForm?.get('zipCode')?.value,
      city: this.mainForm?.get('city')?.value,
      mobilePhone: this.mainForm?.get('mobilePhone')?.value,
      landLine: this.mainForm?.get('landLine')?.value,
      // termsConditionsAccepted: this.mainForm?.get('termsConditionsAccepted')
      //   ?.value,
    };

    if (this.proofOfPayment == ProofOfPaymentOption.Invoice) {
      let invoiceData: InvoiceBuyerInfo = {
        taxNumber: this.invoiceSubForm?.get('taxNumber')?.value,
        companyName: this.invoiceSubForm?.get('companyName')?.value,
        taxOffice: this.invoiceSubForm?.get('taxOffice')?.value,
        profession: this.invoiceSubForm?.get('profession')?.value,
      };
      result.invoiceData = invoiceData;
    }

    this.formDataEvent.emit(result);
  }

  private markAllAsTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markAllAsTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  protected onProofOfPaymentChange(type: ProofOfPaymentOption) {
    this.mainForm?.get('proofOfPayment')?.setValue(type);
  }

  loadOldState() {
    if (this.oldState) {
      this.mainForm?.get('proofOfPayment')?.setValue(this.oldState.proofOfPayment)
      this.buyerForm
        ?.get('mainForm.fullName')
        ?.setValue(this.oldState.fullName);
      this.mainForm?.get('email')?.setValue(this.oldState.email);
      this.mainForm?.get('address')?.setValue(this.oldState.address);
      this.mainForm?.get('zipCode')?.setValue(this.oldState.zipCode);
      this.mainForm?.get('city')?.setValue(this.oldState.city);
      this.mainForm?.get('mobilePhone')?.setValue(this.oldState.mobilePhone);
      this.mainForm?.get('landLine')?.setValue(this.oldState.landLine);
      // this.mainForm
      //   ?.get('termsConditionsAccepted')
      //   ?.setValue(this.oldState.termsConditionsAccepted);
      // this.mainForm
      //   ?.get('paymentsTermsAccepted')
      //   ?.setValue(this.oldState.termsConditionsAccepted);
      // this.mainForm
      //   ?.get('serviceTermsAccepted')
      //   ?.setValue(this.oldState.termsConditionsAccepted);
      this.invoiceSubForm?.get('taxNumber')?.setValue(this.oldState?.invoiceData?.taxNumber);
      this.invoiceSubForm?.get('companyName')?.setValue(this.oldState?.invoiceData?.companyName);
      this.invoiceSubForm?.get('taxOffice')?.setValue(this.oldState?.invoiceData?.taxOffice);
      this.invoiceSubForm?.get('profession')?.setValue(this.oldState?.invoiceData?.profession);
    }
  }

  controlHasError(formAndControlName: string): boolean {
    let result = false;
    const formControl = this.buyerForm.get(formAndControlName);
    const isInvalid = formControl ? !formControl.valid : false;
    const isTouched = formControl ? formControl.touched : false;
    result = isInvalid && isTouched;
    return result;
  }

  onPickCountry(selectedValue: string) {
    this.countryCode = selectedValue;
  }
}
